import React from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import SafeIcon from '../../common/SafeIcon';

const PortalLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/portal/login');
  };

  const navItems = {
    participant: [
      { name: 'Dashboard', path: '/portal/dashboard', icon: 'Grid' },
      { name: 'My Certificates', path: '/portal/participant/certificates', icon: 'Award' },
      { name: 'My Mentoring', path: '/portal/participant/mentoring', icon: 'Target' },
    ],
    manager: [
      { name: 'Dashboard', path: '/portal/dashboard', icon: 'Grid' },
      { name: 'Team Overview', path: '/portal/manager/team', icon: 'Users' },
      { name: 'Compliance Matrix', path: '/portal/manager/compliance', icon: 'CheckSquare' },
    ],
    compliance: [
      { name: 'Dashboard', path: '/portal/dashboard', icon: 'Grid' },
      { name: 'Compliance Records', path: '/portal/manager/compliance', icon: 'CheckSquare' },
      { name: 'Timing Logs', path: '/portal/dashboard', icon: 'Clock' },
    ],
    admin: [
      { name: 'Dashboard', path: '/portal/dashboard', icon: 'Grid' },
      { name: 'Organisations', path: '/portal/dashboard', icon: 'Briefcase' },
      { name: 'System Settings', path: '/portal/admin/settings', icon: 'Settings' },
    ]
  };

  const items = navItems[user?.role] || [];

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-navy-900 text-white flex flex-col fixed h-full z-20">
        <div className="p-6 border-b border-navy-800">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-gold-500 rounded flex items-center justify-center">
              <span className="text-navy-900 font-serif font-bold">K</span>
            </div>
            <span className="font-serif font-bold text-lg">Portal</span>
          </div>
          <p className="text-xs text-slate-400 truncate">{user?.org}</p>
        </div>

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          <p className="text-xs uppercase tracking-wider text-slate-500 mb-4 px-2 font-semibold">Menu</p>
          {items.map((item) => (
            <Link
              key={item.name}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                location.pathname === item.path
                  ? 'bg-navy-800 text-white border-l-4 border-gold-500'
                  : 'text-slate-300 hover:bg-navy-800 hover:text-white'
              }`}
            >
              <SafeIcon name={item.icon} className={`w-5 h-5 ${location.pathname === item.path ? 'text-gold-500' : ''}`} />
              <span className="font-medium text-sm">{item.name}</span>
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-navy-800 bg-navy-950/50">
          <div className="flex items-center gap-3 mb-4 px-2">
            <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-xs font-bold ring-2 ring-gold-500/20">
              {user?.profile?.first_name?.charAt(0)}{user?.profile?.last_name?.charAt(0)}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.profile?.first_name} {user?.profile?.last_name}</p>
              <p className="text-[10px] text-slate-500 uppercase font-bold tracking-tight">{user?.role?.replace('_', ' ')}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm text-red-400 hover:bg-red-900/20 rounded transition-colors"
          >
            <SafeIcon name="LogOut" className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 ml-64 p-8 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
};

export default PortalLayout;